import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

type LegalSection = {
  id: string;
  label: string;
};

type LegalPageLayoutProps = {
  title: string;
  description: string;
  lastUpdated: string;
  sections: LegalSection[];
  children: ReactNode;
};

const legalLinks = [
  { to: '/privacy', label: 'Privacy Policy' },
  { to: '/terms', label: 'Terms of Service' },
  { to: '/data-deletion', label: 'Data Deletion' },
];

export function LegalPageLayout({
  title,
  description,
  lastUpdated,
  sections,
  children,
}: LegalPageLayoutProps) {
  return (
    <main className="min-h-screen overflow-x-hidden bg-canvas text-primary">
      <div className="mx-auto flex w-full min-w-0 max-w-6xl flex-col gap-8 px-4 py-8 sm:px-6 lg:px-8">
        <header className="min-w-0 border-b border-border-subtle pb-6">
          <Link
            to="/"
            className="text-sm font-semibold text-brand hover:text-brand/80"
          >
            AdVerdict
          </Link>
          <div className="mt-6 min-w-0 max-w-3xl">
            <h1 className="break-words text-4xl font-black leading-tight text-primary">
              {title}
            </h1>
            <p className="mt-3 break-words text-base leading-7 text-secondary">
              {description}
            </p>
            <p className="mt-4 text-sm font-medium text-muted">
              Last updated: {lastUpdated}
            </p>
          </div>
        </header>

        <div className="grid min-w-0 gap-8 lg:grid-cols-[240px_minmax(0,1fr)]">
          <nav
            aria-label={`${title} sections`}
            className="h-fit min-w-0 rounded-xl border border-border-subtle bg-surface p-4 shadow-sm lg:sticky lg:top-8"
          >
            <p className="text-xs font-semibold uppercase text-muted">
              Sections
            </p>
            <ol className="mt-3 space-y-2 text-sm">
              {sections.map((section) => (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    className="block rounded-md px-2 py-1.5 text-secondary hover:bg-surface-subtle hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/20"
                  >
                    {section.label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <article className="min-w-0 rounded-xl border border-border-subtle bg-surface p-5 shadow-sm sm:p-8">
            <div className="min-w-0 space-y-8 break-words text-sm leading-7 text-secondary">
              {children}
            </div>
          </article>
        </div>

        <footer className="border-t border-border-subtle py-6">
          <nav
            aria-label="Legal pages"
            className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm"
          >
            {legalLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="font-medium text-secondary hover:text-brand"
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </footer>
      </div>
    </main>
  );
}

export default LegalPageLayout;
